import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Action, Store } from '@ngrx/store';
import { MojoFeatureExtensionsService } from 'mojo-feature-extensions';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { AssociationByCandidateIdJobIdObj } from '@app/collections/associations';
import { getAssociationLabel } from '@collections/association-label';
import {
  ChangeCandidateStatusModalComponent,
  ChangeCandidateStatusModalData,
} from '@modules/assigns/components/change-candidate-status-modal/change-candidate-status-modal.component';
import { getMoveForwardPhases, SelectedPhaseObj } from '@modules/assigns/store';
import { AssociationLabel, AssociationStatusConfiguration, CandidateAdapter } from '@shared/models';

@Component({
  selector: 'mojo-move-forward-button',
  templateUrl: './move-forward-button.component.html',
  styleUrls: ['./move-forward-button.component.scss'],
})
export class MoveForwardButtonComponent implements OnInit {
  @Input() candidate: CandidateAdapter;
  @Input() jobId: number;
  @Input() afterMoveForwardActions: Action[] = [];

  isDirectApplicant$: Observable<boolean>;

  constructor(
    private dialog: MatDialog,
    private store: Store<{}>,
    private featureExtensionsService: MojoFeatureExtensionsService,
  ) {}

  moveForwardButtonClicked() {
    combineLatest([
      this.featureExtensionsService.hasFeatureExtension(
        AppFeatureExtensions.ASSOCIATION_FORWARD_ONLY,
      ),
      this.store.select(
        AssociationByCandidateIdJobIdObj.getAssociationByCandidateIdAndJobId(
          this.candidate.id,
          this.jobId,
        ),
      ),
    ])
      .pipe(
        switchMap(([moveForwardOnly, association]) =>
          this.store
            .select(getMoveForwardPhases, {
              jobId: this.jobId,
              currentAssociation: association,
              moveForwardOnly,
            })
            .pipe(
              map(
                ([phase, availableSteps]: [
                  SelectedPhaseObj,
                  AssociationStatusConfiguration[],
                ]) => ({
                  phase,
                  availableSteps,
                  moveForwardOnly,
                }),
              ),
            ),
        ),
        take(1),
      )
      .subscribe(({ phase, availableSteps, moveForwardOnly }) => {
        this.dialog.open<ChangeCandidateStatusModalComponent, ChangeCandidateStatusModalData>(
          ChangeCandidateStatusModalComponent,
          {
            panelClass: 'medium-size-modal',
            disableClose: true,
            data: {
              headerText: 'Moving to shortlist',
              candidateId: this.candidate.id,
              jobId: this.jobId,
              initialPhase: phase.status,
              initialState: phase.subStatus,
              availableSteps,
              totalMatchesAssign: true,
              actions: this.afterMoveForwardActions,
              moveForwardOnly,
            },
          },
        );
      });
  }

  ngOnInit() {
    const props = {
      candidateId: this.candidate.id,
      jobId: this.jobId,
    };

    this.isDirectApplicant$ = this.store.select(getAssociationLabel, props).pipe(
      withLatestFrom(
        this.featureExtensionsService.hasFeatureExtension(
          AppFeatureExtensions.ASSOCIATION_DIRECT_APPLICANT,
        ),
      ),
      map(([label, includesExtension]: [string, boolean]) =>
        includesExtension ? label === AssociationLabel.DIRECT : true,
      ),
      shareReplay(1),
    );
  }
}
